import { NetworkErrorCode, WsNetworkError } from '../wsErrors/WsNetworkError';
import { Listener, Stream } from './Stream';
import { WsDisconnectCode } from './WebsocketService';
import { NetworkEventType, WsNetworkService } from './WsNetworkService';

export class WsController {
  private closeStream = new Stream<CloseEvent>();
  private url: string;
  private binaryType?: BinaryType;
  private errorAttempts = 0;

  constructor(private networkService: WsNetworkService) {
    this.networkService.on(NetworkEventType.Close, (event: CloseEvent) =>
      this.handleWsClose(event)
    );
    this.networkService.on(NetworkEventType.Open, (event: Event) => this.handleWsOpen(event));
    this.networkService.on(NetworkEventType.PingPongError, (event: WsNetworkError) =>
      this.handleWsPingPongError(event)
    );
  }

  public onClose(listener: Listener<CloseEvent>): () => void {
    return this.closeStream.on(listener);
  }

  public offClose(listener: Listener<CloseEvent>): void {
    this.closeStream.off(listener);
  }

  public connect(url: string, binaryType?: BinaryType, withoutPingPong?: boolean): void {
    this.url = url;
    this.binaryType = binaryType;
    this.networkService.connect(url, binaryType, withoutPingPong);
  }

  public disconnect(code?: WsDisconnectCode): void {
    this.networkService.disconnect(code);
  }

  private handleWsPingPongError(error: WsNetworkError): void {
    this.networkService.disconnect(WsDisconnectCode.ManualClose);
    this.handleWsError(error);
  }

  private handleWsError(error: WsNetworkError): void {
    this.networkService.error(error);

    this.errorAttempts = Math.min(4, this.errorAttempts + 1);

    const reconnectTime = 1000 * Math.pow(2, this.errorAttempts);

    setTimeout(() => {
      this.reconnect();
    }, reconnectTime);
  }

  private handleWsClose(event: CloseEvent): void {
    if (event.code === WsDisconnectCode.NetworkError) {
      const error = new WsNetworkError(NetworkErrorCode.ServiceUnavailable, 'Service unavailable');

      return this.handleWsError(error);
    }

    if (event.code === WsDisconnectCode.UnexpectedError) {
      const error = new WsNetworkError(NetworkErrorCode.InternalError, 'Internal service error');

      return this.handleWsError(error);
    }

    this.closeStream.emit(event);
  }

  private reconnect(): void {
    this.connect(this.url, this.binaryType);
  }

  private handleWsOpen(_event: Event): void {
    this.errorAttempts = 0;
    this.networkService.retry();
  }
}
